// server/browser/src/lib/config.ts
//
// Runtime config fetched from the cloud's /app-config.json (served beside
// the SPA static assets by webapp.py).
//
// The IdP URLs + Paddle public token are NOT baked into the bundle, so the
// cloud can be repointed without an SPA rebuild. Public values only — no
// secrets — preserving the invariant that the cloud never proxies the IdP;
// it only relays URLs (clients reach the IdP directly during PKCE login).

import type { OidcConfig } from "./auth";
import { platformOrNull } from "./remote/platform";

// iOS shells run inside a Tauri WebView whose origin is local, not the
// cloud, so the OIDC round-trip uses this registered custom scheme and
// returns via a deep link instead of /auth/callback.
const IOS_REDIRECT_URI = "optio://callback";

interface AppConfigPayload {
  authorize_url: string;
  token_url: string;
  client_id: string;
  client_secret?: string;
  end_session_url?: string;
  scope?: string;
  paddle_client_token?: string;
  paddle_environment?: string;
  site_base_url?: string;
  billing_enabled?: boolean;
  remote_enabled?: boolean;
}

export interface AppConfig {
  oidc: OidcConfig;
  paddleClientToken: string;
  paddleEnvironment: string;
  siteBaseUrl: string;
  billingEnabled: boolean;
  remoteEnabled: boolean;
}

let cached: AppConfig | null = null;

/** True when running inside the native iOS Tauri shell. */
export function isNativeIos(): boolean {
  return platformOrNull()?.kind === "ios";
}

/**
 * Whether in-app purchase/billing surfaces (Paddle checkout + customer
 * portal) may be shown. Always off on iOS — Apple App Store guideline 3.1.1
 * forbids non-IAP digital-purchase flows in-app — and otherwise follows the
 * cloud's `billing_enabled` flag.
 */
export function commerceEnabled(cfg: AppConfig): boolean {
  return cfg.billingEnabled && !isNativeIos();
}

function computeRedirectUri(): string {
  if (platformOrNull()?.kind === "ios") return IOS_REDIRECT_URI;
  if (typeof window === "undefined") return "";
  // The OIDC callback lands on /auth/callback (fragment-free, which
  // Keycloak requires). The client router handles it from there.
  return `${window.location.origin}/auth/callback`;
}

function fromPayload(body: AppConfigPayload, redirect: string): AppConfig {
  return {
    oidc: {
      authorizeUrl: body.authorize_url,
      tokenUrl: body.token_url,
      clientId: body.client_id,
      clientSecret: body.client_secret,
      redirectUri: redirect,
      endSessionUrl: body.end_session_url || "",
      scope: body.scope || "openid email profile",
    },
    paddleClientToken: body.paddle_client_token || "",
    paddleEnvironment: body.paddle_environment || "sandbox",
    siteBaseUrl: body.site_base_url || "",
    billingEnabled: body.billing_enabled ?? true,
    remoteEnabled: body.remote_enabled ?? true,
  };
}

// The single public runtime-config endpoint served beside the SPA.
const CONFIG_PATH = "/app-config.json";

/** Thrown when /app-config.json can't be read. Carries the attempted URL so
 *  the caller (and the user) can see exactly what failed — e.g. an iOS
 *  Tauri http capability misallowlist or a wrong baked `VITE_API_BASE_URL`.
 *  The old behaviour was to silently substitute a dev fallback pointing at
 *  http://localhost:8000/token; that swallowed real bugs and turned login
 *  into a mysterious "connection refused" in the user's browser. */
export class AppConfigUnavailableError extends Error {
  readonly url: string;
  readonly cause?: unknown;
  constructor(url: string, message: string, cause?: unknown) {
    super(message);
    this.name = "AppConfigUnavailableError";
    this.url = url;
    this.cause = cause;
  }
}

export async function resolveAppConfig(): Promise<AppConfig> {
  if (cached) return cached;
  const redirect = computeRedirectUri();
  // iOS reads config from the baked cloud origin (its WebView origin is
  // local). Browser: the adapter returns window.location.origin in the
  // remote section, or there's no adapter at all on site/admin — both
  // resolve to a same-origin fetch identical to the previous behaviour.
  const base = platformOrNull()?.getCloudBaseUrl() ?? "";
  const url = `${base}${CONFIG_PATH}`;
  let res: Response;
  try {
    res = await fetch(url, { headers: { accept: "application/json" } });
  } catch (e) {
    // Network-level failure (Tauri http allowlist denial, DNS, refused
    // connection, CORS). Surface it instead of swapping in a fake config.
    throw new AppConfigUnavailableError(
      url,
      `Couldn't reach the cloud at ${url}. Check that the cloud is running and (on iOS) that this host is in the http capabilities allowlist.`,
      e,
    );
  }
  if (!res.ok) {
    throw new AppConfigUnavailableError(
      url,
      `Cloud returned HTTP ${res.status} for ${url}.`,
    );
  }
  let body: AppConfigPayload;
  try {
    body = (await res.json()) as AppConfigPayload;
  } catch (e) {
    throw new AppConfigUnavailableError(
      url,
      `Cloud returned non-JSON for ${url}.`,
      e,
    );
  }
  cached = fromPayload(body, redirect);
  return cached;
}

export function __setAppConfigForTests(config: AppConfig | null): void {
  cached = config;
}
